import { client } from 'js/src/client';
import { ShowRequestErrorModal } from 'js/src/components/common/RequestErrorModal';
import { INFINITE_TOAST_DURATION, Toast } from 'js/src/components/common/Toast';
import { ApiError, isUndefined } from 'js/src/libs/consts';
import { waitForever, waitForMillis } from 'js/src/libs/custom-utils';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Navigation from 'js/src/libs/navigation';
import uuid from 'js/src/libs/uuid';
import Cookies from 'js-cookie';

function getCSRFToken(): string {
    const csrfToken = Cookies.get('csrftoken');
    return isUndefined(csrfToken) ? '' : csrfToken;
}

function getWorkspaceId(): string {
    return isUndefined(SERVER?.USER?.workspace_id) ? '' : `${SERVER.USER.workspace_id}`;
}

/*
 * Compress a string using the CompressionStream API
 */
async function compressWithCompressionStream(input: string): Promise<Blob> {
    const encoder = new TextEncoder();
    const inputStream = new ReadableStream({
        start(controller) {
            controller.enqueue(encoder.encode(input));
            controller.close();
        },
    });

    const compressedStream = inputStream.pipeThrough(new CompressionStream('gzip'));
    const response = new Response(compressedStream);
    return response.blob();
}

/*
 * Compress large request bodies (saving 90-95% bandwidth)
 */
export async function gzipBigRequests(
    request: Request,
    thresholdBytes = 300 * 1024,
): Promise<Request> {
    try {
        const method = request.method.toUpperCase();

        if (['POST', 'PUT', 'PATCH'].includes(method)) {
            const originalBody = await request.clone().text();
            const encoded = new TextEncoder().encode(originalBody);

            if (encoded.length > thresholdBytes) {
                const compressedBlob = await compressWithCompressionStream(originalBody);

                const newHeaders = new Headers(request.headers);
                newHeaders.set('Content-Encoding', 'gzip');

                return new Request(request.url, {
                    method,
                    headers: newHeaders,
                    body: compressedBlob,
                    credentials: request.credentials,
                    cache: request.cache,
                    redirect: request.redirect,
                    referrer: request.referrer,
                    referrerPolicy: request.referrerPolicy,
                    integrity: request.integrity,
                    keepalive: request.keepalive,
                    signal: request.signal,
                    mode: request.mode,
                });
            }
        }

        return request;
    } catch (error) {
        ErrorReporter.capture(error);
        return request;
    }
}

const _originalFetch = window.fetch;
export class FetchRetry {
    private static ongoingRetries: Set<string> = new Set();

    public static originalFetch(input: RequestInfo | URL, init?: RequestInit) {
        return _originalFetch(input, init);
    }

    public static isNetworkError(error: unknown): boolean {
        return (
            error instanceof TypeError &&
            error.message.trim().toLowerCase().includes('failed to fetch')
        );
    }

    public static async fetchWithRetry(
        input: RequestInfo | URL,
        init?: RequestInit,
        maxRetries = 3,
    ): Promise<Response> {
        const requestGuid = uuid();
        const retryPromise = (async (): Promise<Response> => {
            for (let attempt = 0; attempt <= maxRetries; attempt++) {
                try {
                    let requestInput: RequestInfo | URL;
                    if (input instanceof Request) {
                        // clone Request object to avoid "already been used" error
                        requestInput = input.clone();
                    } else {
                        requestInput = input;
                    }

                    const response = await FetchRetry.originalFetch(requestInput, init);
                    if (
                        attempt > 0 &&
                        FetchRetry.ongoingRetries.has(requestGuid) &&
                        [...FetchRetry.ongoingRetries][0] === requestGuid
                    ) {
                        Toast.info(
                            'Successfully reconnected!',
                            'Connection Established',
                            'network-error-toast',
                            3000,
                        );
                    }
                    FetchRetry.ongoingRetries.delete(requestGuid);
                    return response;
                } catch (error) {
                    if (FetchRetry.isNetworkError(error)) {
                        FetchRetry.ongoingRetries.add(requestGuid);
                        if (attempt < maxRetries) {
                            // Show countdown for retry attempts
                            const waitDurationSeconds = Math.pow(3, attempt);

                            for (
                                let remainingSeconds = waitDurationSeconds;
                                remainingSeconds > 0;
                                remainingSeconds--
                            ) {
                                if (
                                    FetchRetry.ongoingRetries.has(requestGuid) &&
                                    [...FetchRetry.ongoingRetries][0] === requestGuid
                                ) {
                                    Toast.error(
                                        `Connection failed. Retrying in ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}...`,
                                        'Connection Issue',
                                        'network-error-toast',
                                        INFINITE_TOAST_DURATION,
                                    );
                                }
                                await waitForMillis(1000);
                            }
                        }

                        if (attempt === maxRetries) {
                            ShowRequestErrorModal();
                            // never resolve this fetch, let the user reload manually with RequestErrorModal.
                            Toast.removeAll();
                            await waitForever();
                        }
                    } else {
                        FetchRetry.ongoingRetries.delete(requestGuid);
                        throw error;
                    }
                }
            }
            FetchRetry.ongoingRetries.delete(requestGuid);
            throw new Error('Unreachable code reached.');
        })();

        return retryPromise;
    }
}

client.interceptors.request.use(async (request) => {
    request.headers.set('Content-Type', 'application/json');

    // add the csrf token to the headers in all requests, so that django's
    // csrf protection will not reject requests sent via the OpenAPI client
    request.headers.set('X-CSRFToken', getCSRFToken());

    // add the Lumen5 workspace id to the headers in all requests, so that
    // the backend can determine which workspace the request is for
    request.headers.set('X-LUMEN5-WORKSPACE', getWorkspaceId());

    // for large requests, we gzip the body to reduce the size of the request
    return gzipBigRequests(request);
});

client.interceptors.error.use(async (error: any, response, request, opts) => {
    if (
        response.status === 403 &&
        error?.detail === 'Authentication credentials were not provided.'
    ) {
        Navigation.reloadPage();
        Toast.error(
            'You have been logged out. Please refresh and log in again.',
            undefined,
            'authentication-credentials-were-not-provided',
            INFINITE_TOAST_DURATION,
        );
        await waitForever();
    }
    if (!(error instanceof Error)) {
        return new ApiError(response.status, error, opts.url);
    }
    return error;
});

client.setConfig({
    throwOnError: true,
});
